import { api } from './api'

/** Jednorazowe powiadomienie po udanej mutacji publikacji. Powłoka używa go do
 * odświeżenia kropek na tej samej trasie; to zdarzenie, nie kolejny poller. */
export const PUBLISHING_CHANGED = 'edimi:publishing-changed'
export type PublishingChange = 'connections' | 'campaigns'

export function notifyPublishingChanged(kind: PublishingChange): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent<PublishingChange>(PUBLISHING_CHANGED, { detail: kind }))
}

export type PublishingStatus = {
  available: boolean
  provider: string
}

export type PublishingConnection = {
  id: number
  platform: string
  display_name: string
  external_account_id: string
  status: string
  slot_index?: number | null
  slot_reserved_until?: string | null
  provider_ref?: Record<string, unknown>
  /** Session 22: a signed copy of the account's picture, or null — the UI draws the initial. */
  avatar_url?: string | null
  nickname?: string | null
}

/** Session 22: a paid slot still held by an account the artist let go of (one line per identity). */
export type ReservedSlot = {
  connection_id: number
  platform: string
  display_name: string | null
  reserved_until: string
  /** Can THIS adapter actually take the account back?
   *
   * The way back is a promise about the world, not a button the screen may assume: an
   * adapter without `reauth_url` answers the re-auth with a plain connect link, the artist
   * walks the whole OAuth, and the disconnected row stays disconnected. The backend knows
   * which adapter is serving and says so per entry; the screen draws the button only when
   * the answer is yes, and otherwise leaves the line as the billing fact it is. */
  can_reconnect?: boolean
}

/**
 * Session 23: the three platforms publishing supports, in the order they were rolled out.
 * The same order the platform dialog offers and the same keys the backend validates
 * (`POST /api/publishing/connect`), the glyph set and `appAccounts.platform.*` use.
 */
export const PUBLISH_PLATFORMS = ['tiktok', 'instagram', 'youtube'] as const
export type PublishPlatform = (typeof PUBLISH_PLATFORMS)[number]

/**
 * The known platform behind a `platform` field, or null.
 *
 * `platform` stays a plain string on the wire: an older row, or a value this build has
 * not learned yet, must not be cast into a union the screen then draws a logo for. Asking
 * through this is how a screen gets a glyph name, a label key and a locative — and how an
 * unknown platform ends up with none of them instead of somebody else's logo.
 */
export function platformKey(platform?: string | null): PublishPlatform | null {
  const key = (platform ?? '').toLowerCase()
  return (PUBLISH_PLATFORMS as readonly string[]).includes(key) ? (key as PublishPlatform) : null
}

/**
 * Does this platform's `display_name` carry an „@” handle?
 *
 * YouTube's does not: it is the CHANNEL NAME the artist picked in the Google window
 * (MAPA 23, decyzja 2), so an „@” in front of it would be an address nobody can type.
 */
export function platformHasHandle(platform?: string | null): boolean {
  return platformKey(platform) !== 'youtube'
}

export async function fetchPublishingStatus(): Promise<PublishingStatus> {
  return api<PublishingStatus>('/api/publishing/status')
}

export async function fetchPublishingConnections(): Promise<PublishingConnection[]> {
  const body = await api<{ items: PublishingConnection[] }>('/api/publishing/connections')
  // `?? []` because the caller maps over this and a missing key would throw during render.
  // With no error boundary anywhere in the app, one such throw unmounts the whole root —
  // sidebar included — and the artist gets a white rectangle, not a broken card. Measured
  // as a class this morning: five libraries returned `r.items` bare and one screen was
  // already white on the walker's frames.
  return body.items ?? []
}

/** The accounts page reads both halves: the rows it draws and the reserved slots under the meter. */
export async function fetchPublishingAccounts(): Promise<{ items: PublishingConnection[]; reserved: ReservedSlot[] }> {
  const body = await api<{ items?: PublishingConnection[]; reserved?: ReservedSlot[] }>('/api/publishing/connections')
  return { items: body.items ?? [], reserved: body.reserved ?? [] }
}

/** What the platform currently allows this creator to do.
 *
 * The composer's visibility dropdown is built FROM `privacy_level_options` and from
 * nothing else — never a hardcoded list. A creator who has switched their profile to
 * private genuinely has fewer options today than yesterday, and a menu that still
 * offers the old ones would be offering something the post cannot have.
 */
export type CreatorInfo = {
  nickname: string
  avatar_url: string
  privacy_level_options: string[]
  comment_disabled: boolean
  duet_disabled: boolean
  stitch_disabled: boolean
  max_video_post_duration_sec?: number | null
}

/** What the artist agreed to, for one specific post.
 *
 * The nine fields at the top are the shared record every platform is written into —
 * they carry TikTok's controls, and on a platform that has no such control they stay
 * at their off value rather than becoming a silence (`app/publishing/consent.py`).
 *
 * Since session 23 a post is a TikTok post, a Reel or a Short, so the controls that
 * exist on ONE platform only are optional here and travel in that platform's own
 * payload: `share_to_feed` for a Reel, `title` / `made_for_kids` /
 * `contains_synthetic_media` for a Short.
 */
export type PostConsent = {
  privacy_level: string
  content_preview_confirmed: boolean
  allow_comment: boolean
  allow_duet: boolean
  allow_stitch: boolean
  disclosure_enabled: boolean
  brand_organic: boolean
  brand_content: boolean
  is_aigc: boolean
  /** Instagram: show the Reel in the feed as well. ON is Instagram's own value. */
  share_to_feed?: boolean
  /** YouTube: the Short's title — required, and at most 100 characters. */
  title?: string
  /** YouTube's COPPA declaration, and deliberately THREE-state: absent means the
   *  artist has not answered, and nobody may answer that one on their behalf. */
  made_for_kids?: boolean
  /** YouTube: content that could be mistaken for real. Starts off, like the AI label. */
  contains_synthetic_media?: boolean
}

/** Throughput: a shared monthly post allowance, or the vendor's live rate-limit window. */
export type ProviderQuota = {
  used: number
  limit: number
  percent: number
  period: string
  /** Only ever true for a monthly allowance — a per-minute throttle never blocks planning. */
  planning_blocked: boolean
  source: 'monthly_posts' | 'rate_limit_window'
}

/** Cost: connected accounts against the vendor's pricing bands (Zernio bills per account). */
export type ProviderAccountTier = {
  accounts: number
  tier: string
  tier_ceiling: number
  headroom: number
  near_next_tier: boolean
}

/** The defaults a fresh composer row starts from.
 *
 * Every one of these is deliberately the "off"/"unchosen" value. `privacy_level` is an
 * empty string rather than a sensible-looking default because an unmade choice must stay
 * visibly unmade — the submit button stays disabled until the artist picks one.
 */
export const EMPTY_CONSENT: PostConsent = {
  privacy_level: '',
  content_preview_confirmed: false,
  allow_comment: false,
  allow_duet: false,
  allow_stitch: false,
  disclosure_enabled: false,
  brand_organic: false,
  brand_content: false,
  is_aigc: false,
}

export function consentIsComplete(consent: PostConsent): boolean {
  if (!consent.privacy_level || !consent.content_preview_confirmed) return false
  if (consent.disclosure_enabled && !consent.brand_organic && !consent.brand_content) return false
  // Branded content cannot be posted privately; offering both is offering a post that
  // will be refused at send time.
  if (consent.brand_content && consent.privacy_level === 'SELF_ONLY') return false
  return true
}

export async function connectPublishingAccount(platform: string, slotIndex: number): Promise<string> {
  const body = await api<{ connect_url: string }>('/api/publishing/connect', {
    method: 'POST',
    body: JSON.stringify({ platform, slot_index: slotIndex }),
  })
  return body.connect_url
}

export async function reauthPublishingAccount(connectionId: number): Promise<string> {
  const body = await api<{ connect_url: string }>(
    `/api/publishing/connections/${connectionId}/reauth`,
    { method: 'POST' },
  )
  return body.connect_url
}

export async function disconnectPublishingAccount(
  connectionId: number,
): Promise<{ status: string; cancelled_posts: number }> {
  const result = await api<{ status: string; cancelled_posts: number }>(
    `/api/publishing/connections/${connectionId}/disconnect`,
    { method: 'POST' },
  )
  notifyPublishingChanged('connections')
  return result
}

/** Live creator options, with the cached copy as an explicit fallback.
 *
 * `stale` is returned rather than hidden: a composer painted from a cache must say so,
 * because the artist is about to make a visibility choice against it.
 */
export async function fetchCreatorInfo(
  connectionId: number,
): Promise<{ creator_info: CreatorInfo | null; stale: boolean; error?: string }> {
  return api(`/api/publishing/connections/${connectionId}/creator-info`)
}

/** Everything the accounts page and the dashboard need, in one local read. */
export type PublishingSummary = {
  slots: {
    /** null means "we could not ask" — never render that as zero. */
    available: number | null
    used: number
    total: number | null
    daily_cap: number
    reserved_disconnected?: number
    period_end?: string
  }
  /** connection_id -> posts already committed for today. */
  today_by_connection: Record<string, number>
  /** connection_id -> open posts (pending / scheduled / publishing) that disconnecting
   *  would cancel — the same predicate as `_open_jobs_for_connection` (paczka 12 §G 4).
   *  Optional: an older server answers without it, and the dialog then says so. */
  scheduled_by_connection?: Record<string, number>
  /** Posts the provider refused to withdraw: charged, unrefunded, awaiting a person. */
  needs_manual_settlement: number
  connections: { connected: number; expired: number; suspended: number; total_slots_in_use: number }
  posts: { published_total: number; published_30d: number; scheduled: number; next_scheduled_at: string | null }
  campaigns: { active: number }
}

export type ActivityEntry = {
  id: number
  kind: string
  status: string
  at: string
  title: string | null
  connection_id: number
  connection_name: string | null
  campaign_id: string | null
  error_class: string | null
  refunded?: boolean
  post_url?: string | null
}

export async function fetchPublishingSummary(): Promise<PublishingSummary> {
  return api<PublishingSummary>('/api/publishing/summary')
}

export async function fetchPublishingActivity(limit = 20): Promise<ActivityEntry[]> {
  const body = await api<{ items: ActivityEntry[] }>(`/api/publishing/activity?limit=${limit}`)
  return body.items ?? []
}

export async function fetchProviderQuota(): Promise<{
  quota: ProviderQuota
  accounts: ProviderAccountTier
}> {
  return api('/api/publishing/quota')
}

/**
 * Inicjał w kółku dla KAŻDEGO uchwytu z listy — litera, po której czytelnik odróżnia konta
 * w planie i w pozycjach (artboardy 07/08/12: N · B · L). Pierwsza litera uchwytu, a gdy dwa
 * uchwyty zaczynają się tak samo (`@nocna.zmiana` / `@nocna.live`), pierwsza litera OSTATNIEGO
 * członu (po kropce, podkreśleniu lub myślniku) — o ile ta jest jeszcze wolna. Dwa konta
 * z tą samą literą nie mówią nic; „N · L” mówi, które jest które.
 */
export function initialsFor(handles: readonly string[]): Record<string, string> {
  const bare = (h: string) => h.replace(/^[@#]/, '')
  const first = (h: string) => (bare(h)[0] ?? '?').toUpperCase()
  const last = (h: string) => {
    const parts = bare(h).split(/[._-]+/).filter(Boolean)
    return (parts[parts.length - 1]?.[0] ?? bare(h)[0] ?? '?').toUpperCase()
  }
  const out: Record<string, string> = {}
  const taken = new Set<string>()
  const unique = [...new Set(handles)]
  for (const h of unique) {
    const f = first(h)
    if (!taken.has(f)) { out[h] = f; taken.add(f) }
  }
  for (const h of unique) {
    if (out[h]) continue
    const l = last(h)
    out[h] = taken.has(l) ? first(h) : l
    taken.add(out[h])
  }
  return out
}
